/*
 * Mappers from staff-domain entities → Calendar events/layer intervals.
 * Used by staff/schedule (My Schedule) and admin/availability (Team).
 */

import { format, parseISO } from 'date-fns';
import type { StaffAvailableSlot, CalendarBlock } from '@/services/api/staff.service';
import type {
  AvailabilityOverride,
  ProviderAvailability,
} from '@/services/api/availability.service';
import type {
  CalendarEvent,
  CalendarLayerInterval,
  EventStatus,
} from './types';

export function isGroupClassSlot(slot: StaffAvailableSlot): boolean {
  const cap = slot.max_capacity ?? slot.service?.max_capacity ?? 1;
  return (cap ?? 1) > 1;
}

function combine(dateStr: string, time: string): Date {
  // dateStr "YYYY-MM-DD", time "HH:MM" or "HH:MM:SS" — local time.
  const [h, m] = time.split(':');
  const d = parseISO(`${dateStr}T00:00:00`);
  d.setHours(parseInt(h, 10), parseInt(m, 10), 0, 0);
  return d;
}

function dayKey(d: Date): string {
  return format(d, 'yyyy-MM-dd');
}

/* ── Slot → CalendarEvent ─────────────────────────────────────────────── */

export function slotToEvent(
  slot: StaffAvailableSlot,
  fallbackTitle: string,
): CalendarEvent | null {
  if (!slot.start_time || !slot.end_time) return null;
  if (isGroupClassSlot(slot)) return null; // surfaced as a layer, not event
  const start = parseISO(slot.start_time);
  const end = parseISO(slot.end_time);
  const status: EventStatus = slot.booked
    ? slot.booking?.status === 'pending'
      ? 'pending'
      : 'confirmed'
    : slot.available
      ? 'confirmed'
      : 'blocked';
  return {
    id: `slot-${slot.id}`,
    start,
    end,
    title: slot.booked
      ? (slot.booking?.customer_full_name ?? fallbackTitle)
      : (slot.service?.name ?? fallbackTitle),
    subtitle: slot.booked ? slot.service?.name : undefined,
    status,
    columnId: dayKey(start),
    meta: { kind: 'slot', slot },
  };
}

export function slotsToEvents(
  slots: StaffAvailableSlot[],
  fallbackTitle: string,
): CalendarEvent[] {
  const out: CalendarEvent[] = [];
  for (const s of slots) {
    const e = slotToEvent(s, fallbackTitle);
    if (e) out.push(e);
  }
  return out;
}

/* ── Group-class slots → layer ────────────────────────────────────────── */

export function groupClassIntervals(
  slots: StaffAvailableSlot[],
): CalendarLayerInterval[] {
  const out: CalendarLayerInterval[] = [];
  for (const s of slots) {
    if (!isGroupClassSlot(s) || !s.start_time || !s.end_time) continue;
    const start = parseISO(s.start_time);
    const end = parseISO(s.end_time);
    const cap = s.max_capacity ?? s.service?.max_capacity ?? 0;
    const cur = s.current_bookings ?? 0;
    const svcName = s.service?.name ?? '';
    const label = svcName ? `${svcName} · ${cur}/${cap}` : `${cur}/${cap}`;
    out.push({
      id: `gc-${s.id}`,
      start,
      end,
      columnId: dayKey(start),
      label,
      data: { kind: 'group-class', slot: s },
    });
  }
  return out;
}

/* ── Availability ranges → layer ──────────────────────────────────────── */

export function availabilityIntervals(
  availabilityByDate: Map<string, ProviderAvailability[]>,
): CalendarLayerInterval[] {
  const out: CalendarLayerInterval[] = [];
  for (const [dateStr, ranges] of availabilityByDate.entries()) {
    for (const r of ranges) {
      const svcNames = r.services?.map((s) => s.name).filter(Boolean) ?? [];
      const label = svcNames.length === 0 ? undefined : svcNames.join(', ');
      out.push({
        id: `avail-${dateStr}-${r.id}`,
        start: combine(dateStr, r.start_time),
        end: combine(dateStr, r.end_time),
        columnId: dateStr,
        label,
        data: { kind: 'availability', dateStr, range: r },
      });
    }
  }
  return out;
}

/* ── Overrides → split into available / blocked layers ────────────────── */

export interface OverrideIntervalGroups {
  available: CalendarLayerInterval[];
  blocked: CalendarLayerInterval[];
}

export function overrideIntervals(
  overrides: AvailabilityOverride[],
): OverrideIntervalGroups {
  const available: CalendarLayerInterval[] = [];
  const blocked: CalendarLayerInterval[] = [];
  for (const o of overrides) {
    if (!o.active) continue;
    const isAvail = o.override_type === 'extra' || o.override_type === 'modified';
    const svcNames = o.services?.map((s) => s.name).filter(Boolean) ?? [];
    const svcLabel = svcNames.length > 0 ? svcNames.join(', ') : undefined;
    const label = svcLabel ?? o.reason ?? undefined;
    if (!o.start_time || !o.end_time) {
      // Full-day unavailable — synthesize 00:00 → 23:59 block.
      blocked.push({
        id: `ovr-${o.id}`,
        start: combine(o.date, '00:00'),
        end: combine(o.date, '23:59'),
        columnId: o.date,
        label,
        data: { kind: 'override', override: o },
      });
      continue;
    }
    const interval: CalendarLayerInterval = {
      id: `ovr-${o.id}`,
      start: combine(o.date, o.start_time),
      end: combine(o.date, o.end_time),
      columnId: o.date,
      label,
      data: { kind: 'override', override: o },
    };
    (isAvail ? available : blocked).push(interval);
  }
  return { available, blocked };
}

/* ── External calendar busy blocks → layer ────────────────────────────── */

// Backend tags Google-sync blocks with reason="calendar_sync". That token is
// not user-facing; treat it as no reason so the caller's fallback (e.g. "Busy")
// is shown instead.
const TECHNICAL_BUSY_REASONS = new Set(['calendar_sync']);

function humanBusyReason(reason: string | null | undefined, fallback?: string): string | undefined {
  if (!reason || TECHNICAL_BUSY_REASONS.has(reason)) return fallback;
  return reason;
}

export function calendarBlockIntervals(
  blocks: CalendarBlock[],
  fallbackLabel?: string,
): CalendarLayerInterval[] {
  return blocks.map((b) => {
    const start = parseISO(b.start_time);
    const end = parseISO(b.end_time);
    return {
      id: `busy-${b.id}`,
      start,
      end,
      columnId: dayKey(start),
      label: humanBusyReason(b.reason, fallbackLabel),
      data: { kind: 'busy', block: b },
    };
  });
}

export { humanBusyReason };
